import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DeepPartial } from 'redux';
import { isEqual } from 'lodash';
import { checkAssetsSummary, checkAvailability } from 'modules/api/programmaticDeal';
import { handleFetchAvailability } from 'store/dealManagement/actions';
import { DealStatus, DealType, ProgrammaticCampaign } from 'components/common/types/Deal.types';
import { Store } from 'components/common/types/Store.types';
import { useDeepEqualMemo } from 'customHooks/useDeepEqualMemo';
import { formatIndexOptimisationWithEmptyDisplayName } from 'utils/formatIndexOptimisation';
import { cancelFunctions } from 'utils/cancelFunctions';
import { getIsMultiTargetingFile } from 'store/dealManagement/selectors/getMultiTargeting';
import { getDealType } from 'store/dealManagement/selectors/getDealType';
import { getIsNewDeal } from 'store/dealManagement/selectors/getIsNewDeal';
import { useAvailabilityFormData } from './useAvailabilityFormData';

export const useCheckAvailability = (): void => {
  const dispatch = useDispatch();
  const { buildFormData, isDealForm } = useAvailabilityFormData();

  const backupFormData = useSelector((state: Store) => state.dealManagement.backupFormData);
  const isNewDeal = useSelector(getIsNewDeal);
  const programmatic = useSelector((state: Store) => state.dealManagement.programmatic);
  const campaignType = useSelector((state: Store) => state.dealManagement.campaignType);
  const commonDeal = useSelector((state: Store) => state.dealManagement.commonDeal);
  const routeFrameCodes = useSelector((state: Store) => state.dealManagement.commonDeal.currentLine.routeFrameCodes);
  const isClientSideAssetFiltering = useSelector(
    (state: Store) => state.dealManagement.plannerSettings.isClientSideAssetFiltering,
  );
  const dealType = useSelector(getDealType);
  const isDealTypeGuaranteed = dealType === DealType.GUARANTEED;

  const isMultiTargetingFileEnabled = useSelector(getIsMultiTargetingFile);

  const isSimplifiedPlanningMode = useSelector(
    (state: Store) => state.dealManagement.plannerSettings.isSimplifiedPlanningMode,
  );
  const isFullAvailabilityRequested = useSelector(
    (state: Store) => state.dealManagement.plannerSettings.isFullAvailabilityRequested,
  );

  const dealData = useDeepEqualMemo<DeepPartial<ProgrammaticCampaign>>({
    ...commonDeal,
    ...programmatic,
    summary: undefined,
    currentLine: {
      ...commonDeal.currentLine,
      proximity: {
        ...commonDeal.currentLine.proximity,
        plannerPoi: [],
        openStreetMapPoi: [],
      },
      adjustedSot: undefined,
      deliveredImpressions: undefined,
      availability: undefined,
      preservedAvailability: undefined,
      preservedAllocation: undefined,
      uploadedFrameLists: [],
      multiTargetingFile: undefined,
      routeFrameCodes: isDealForm || !isClientSideAssetFiltering ? routeFrameCodes : [],
      segment: undefined,
      isCurrentLineWithProposalAllocation: undefined,
      impressionMetrics: undefined,
      isFetchingAvailability: false,
      distributionOfAvailabilityFilter: {},
    },
  }) as DeepPartial<ProgrammaticCampaign>;

  const rawBackupLine = backupFormData.lines.find((line) => line.id === commonDeal.currentLine.id);
  const backupLine = useDeepEqualMemo(
    rawBackupLine
      ? {
          ...rawBackupLine,
          isFetchingAvailability: undefined,
          availability: undefined,
          preservedAvailability: undefined,
          preservedAllocation: undefined,
          multiTargetingFile: undefined,
        }
      : undefined,
  );

  const buildFormDataRef = useRef(buildFormData);
  buildFormDataRef.current = buildFormData;

  useEffect(() => {
    if (isMultiTargetingFileEnabled) return;

    const isRejected = commonDeal.bookingStatusCode === DealStatus.REJECTED;

    const backupLineOptimisations = {
      ...backupLine,
      availability: undefined,
      preservedAvailability: undefined,
      preservedAllocation: undefined,
      multiTargetingFile: undefined,
      indexOptimisation: formatIndexOptimisationWithEmptyDisplayName(backupLine?.indexOptimisation),
    };
    const currentLineOptimisations = {
      ...commonDeal.currentLine,
      isFetchingAvailability: undefined,
      availability: undefined,
      preservedAvailability: undefined,
      preservedAllocation: undefined,
      multiTargetingFile: undefined,
      indexOptimisation: formatIndexOptimisationWithEmptyDisplayName(commonDeal.currentLine.indexOptimisation),
    };

    const isValid =
      isNewDeal ||
      !isEqual(backupLineOptimisations, currentLineOptimisations) ||
      isRejected ||
      isFullAvailabilityRequested;

    if (isValid) {
      const formData = buildFormDataRef.current();
      const handleAvailability =
        isFullAvailabilityRequested || isDealForm || isDealTypeGuaranteed ? checkAvailability : checkAssetsSummary;
      dispatch(handleFetchAvailability(handleAvailability, formData, cancelFunctions));
    }
  }, [
    dealData,
    campaignType,
    isSimplifiedPlanningMode,
    isMultiTargetingFileEnabled,
    isDealForm,
    isDealTypeGuaranteed,
    isNewDeal,
    backupLine,
    isFullAvailabilityRequested,
  ]);
};
